// UploadPowersForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import BondInfoModal from './bondInfoModal';
import BondInfoGrid from './bondInfoGrid';
import ViewFilesModal from '../../shared/ViewFilesModal'; // Placeholder component

const UploadPowersForm = ({ bondDetails, refreshHandler }) => {
  const quotes = bondDetails?.quotes || [];
  const [selectedFiles, setSelectedFiles] = useState([]); // Files selected but not yet uploaded
  const [isBondModalOpen, setIsBondModalOpen] = useState(false);
  const [selectedBondInfo, setSelectedBondInfo] = useState(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isViewFilesModalOpen, setIsViewFilesModalOpen] = useState(false);
  const [fileCount, setFileCount] = useState(0);
  const [powerCount, setPowerCountCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false); // Spinner state for file upload
  const [transactionId, setTransactionId] = useState(null);
  const [triggerRefetch, setTriggerRefetch] = useState(false); // State to trigger BondInfoGrid refetch
  const [totalPowerAmount, setTotalPowerAmount] = useState(0);
  const [bondInfoList, setBondInfoList] = useState([]);

  // Handle fetching file count
  const fetchFileCount = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;

      const response = await fetch(
        `${baseURL}PostingTransferBond/${bondDetails?.transferBondTransactionID}/FileCount`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch file count.');
      }

      const data = await response.json();
      setFileCount(data.count);
      setPowerCountCount(data.powerCount);
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Error fetching file count.');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (bondDetails !== null && bondDetails.transferBondTransactionID !== transactionId) {
        await fetchFileCount();
        setTransactionId(bondDetails.transferBondTransactionID);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bondDetails]);

  // getting total power amounts to check if total bond amount and the power bond amounts match
  useEffect(() => {
    const total = bondInfoList.reduce((acc, info) => acc + (info.bondAmount || 0), 0);
    setTotalPowerAmount(total);
  }, [bondInfoList]);

  // Handle Bond Info Modal
  const handleOpenBondModal = () => {
    setSelectedBondInfo(null);
    setIsBondModalOpen(true);
  };

  const handleCloseBondModal = () => {
    setIsBondModalOpen(false);
  };

  // Callback after saving bond info to refetch the grid
  const handleSaveBondInfo = () => {
    setTriggerRefetch((prev) => !prev); // Toggle to trigger refetch
    fetchFileCount();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '';
    return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Handle editing bond info
  const handleEditBondInfo = (bondInfo) => {
    setSelectedBondInfo(bondInfo);
    setIsBondModalOpen(true);
  };

  // Handle removing files
  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prevSelectedFiles) =>
      prevSelectedFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  // Handle file selection
  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => {
      const isValidType = ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type);
      if (!isValidType) {
        toast.error(`${file.name} is not a supported file type.`);
      }
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
      if (!isValidSize) {
        toast.error(`${file.name} exceeds the 5MB size limit.`);
      }
      return isValidType && isValidSize;
    });
    setSelectedFiles((prevSelectedFiles) => [...prevSelectedFiles, ...validFiles]);
  };

  // Handle file uploads
  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload.');
      return;
    }

    setIsUploading(true); // Start spinner

    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
    const formData = new FormData();

    selectedFiles.forEach((file) => {
      formData.append('files', file);
    });

    try {
      const response = await fetch(
        `${baseURL}PostingTransferBond/${bondDetails.transferBondTransactionID}/UploadFiles`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload files.');
      }

      toast.success('Files uploaded successfully.');
      setSelectedFiles([]); // Clear selected files
      await fetchFileCount(); // Refresh file count
      refreshHandler(); // Refresh bond details if necessary
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'An error occurred while uploading files.');
    } finally {
      setIsUploading(false); // Stop spinner
    }
  };

  const getAcceptedQuoteMessage = () => {
    const acceptedQuoteId = bondDetails?.acceptedQuoteID;
    const acceptedQuote = quotes.find((quote) => quote.transferBondQuoteID === acceptedQuoteId);
    if (!acceptedQuote) return 'No accepted quote found.';

    const postedByDate = addPostingTimeToAcceptDate(
      acceptedQuote.acceptedDate,
      acceptedQuote.promisedPostingTimeInMinutes
    );

    return (
      <>
        Your quote stated you could post by <b>{postedByDate}</b>.
        Please contact Bankers if you have any issues.
      </>
    );
  };

  const addPostingTimeToAcceptDate = (acceptDate, postingTimeInMins) => {
    const acceptDateObj = new Date(acceptDate);
    const postingTimeInHours = postingTimeInMins / 60;
    acceptDateObj.setHours(acceptDateObj.getHours() + postingTimeInHours);

    const formattedDate =
      acceptDateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }) +
      ' ' +
      acceptDateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    return formattedDate;
  };

  // Handle "Ready for Verification"
  const handleReadyForVerification = async () => {
    // Check if at least one bond info entry exists
    if (triggerRefetch === false && bondDetails.transferBondTransactionID === null) {
      toast.error('Please add at least one bond information entry.');
      return;
    }

    if (fileCount === 0) {
      toast.error('Please add at least one file to upload for verification.');
      return;
    }

    if (Number(bondDetails?.totalBondAmount) !== Number(totalPowerAmount)) {
      toast.error(
        `The total bond amount (${formatCurrency(bondDetails?.totalBondAmount)}) does not match the total from powers (${formatCurrency(totalPowerAmount)}). Please correct this before proceeding.`
      );
      return;
    }

    // Proceed to set ready for verification
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmVerification = async () => {
    setIsSubmitting(true);
    setIsConfirmDialogOpen(false);

    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${baseURL}PostingTransferBond/${bondDetails.transferBondTransactionID}/ReadyForVerification`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to set ready for verification.');
      }

      toast.success('Transaction marked as ready for verification.');
      refreshHandler(); // Refresh bond details
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'An error occurred while setting ready for verification.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelVerification = () => {
    setIsConfirmDialogOpen(false);
  };

  // Handle View Files Modal
  const handleOpenViewFilesModal = () => {
    setIsViewFilesModalOpen(true);
  };

  const handleCloseViewFilesModal = () => {
    setIsViewFilesModalOpen(false);
  };

  return (
    <>
      {/* Header with "View Uploaded Files" button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {bondDetails?.statusCd === "C" ? "Transaction Closed" :  bondDetails?.hasBeenPosted ? 'Awaiting Verification' : 'Upload Powers and Enter Bond Information' }
        </Typography>
        <Tooltip title="View Uploaded Files">
          <IconButton color="primary" onClick={handleOpenViewFilesModal}>
            <VisibilityIcon />
          </IconButton>
          <span style={{ marginLeft: 5 }}>View Uploaded Files</span>
        </Tooltip>
      </Box>
      {/* In verification*/}
      {(bondDetails?.hasBeenPosted && bondDetails?.statusCd !== 'C') && 
      <Box >
          <Typography variant="body1" sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>
            You have submitted the bond and file information for verification. Bankers will verify the details. Thank you! We will reach out if we need any more information.
          </Typography>
      </Box>
      }
      {(bondDetails?.statusCd === 'C') && 
      <Box >
          <Typography variant="body1" sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>
            This transaction is closed, below is the information for reference only.
          </Typography>
      </Box>
      }
      {/* Display Information */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
          <InfoIcon color="primary" sx={{ mr: 0.5, fontSize: '1rem' }} />
          Expected Bonds required: <b>{bondDetails?.numberOfBonds}</b> and Total Bond($) Amount:{' '}
          <b>${formatCurrency(bondDetails?.totalBondAmount)}</b>. If the actual numbers are different, Bankers can
          adjust your fees during verification.
        </Typography>
        {bondDetails?.hasBeenPosted === false && 
        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
          <InfoIcon color="primary" sx={{ mr: 0.5, fontSize: '1rem' }} />
          {getAcceptedQuoteMessage()}
        </Typography>
        }
        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
          <InfoIcon color="primary" sx={{ mr: 0.5, fontSize: '1rem' }} />
          Current Power Count: <b>{powerCount}</b>
        </Typography>
        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
          <InfoIcon color="primary" sx={{ mr: 0.5, fontSize: '1rem' }} />
          Current File Count: <b>{fileCount}</b>
        </Typography>

        {/* Warning Message Before Power Upload */}
        {bondDetails?.hasBeenPosted === false && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1 }}>
            <Typography variant="body1" sx={{ color: '#856404', fontWeight: 'bold' }}>
              <InfoIcon sx={{ mr: 1, fontSize: '1.2rem', verticalAlign: 'middle', color: '#ffc107' }} />
              No changes can be made to power and bond information after it has been uploaded. If any changes are needed, please contact Bankers at 877-778-5245.
            </Typography>
          </Box>
        )}

        {/* File Selection and Upload */}
        <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
          Choose Files
          <input
            type="file"
            hidden
            multiple
            accept="image/*,application/pdf"
            onChange={handleFileSelection}
          />
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleFileUpload}
          disabled={isUploading || selectedFiles.length === 0}
          sx={{ ml: 2 }}
        >
          {isUploading ? <CircularProgress size={24} /> : 'Upload Files'}
        </Button>
      </Box>

      {/* List Selected Files */}
      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Files to Upload:</Typography>
          {selectedFiles.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 1,
              }}
            >
              <CheckCircleIcon sx={{ color: 'green', mr: 1 }} />
              <Typography>{file.name}</Typography>
              <IconButton
                aria-label="remove file"
                onClick={() => handleRemoveFile(index)}
                size="small"
                sx={{ ml: 2 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Add Bond Information Button */}
      {/* <Box sx={{ mt: 4 }}>
        <Button variant="contained" color="primary" onClick={handleOpenBondModal} disabled={bondDetails?.hasBeenPosted}>
          Add Bond Information
        </Button>
      </Box> */}

      {/* Bond Info Grid */}
      <BondInfoGrid
        transactionId={bondDetails?.transferBondTransactionID}
        onEditBondInfo={handleEditBondInfo}
        triggerRefetch={triggerRefetch}
        canEdit={bondDetails?.hasBeenPosted}
        onBondInfoUpdate={setBondInfoList}
      />

      {/* Ready for Verification Button */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>  {/* adding box around both buttons for layout consistency */}
      {bondDetails?.hasBeenPosted === false && (
        <Button
          variant="contained"
          color="secondary"
          onClick={handleReadyForVerification}
          disabled={isSubmitting || fileCount < 1 || powerCount < 1 || Number(bondDetails?.totalBondAmount) !== Number(totalPowerAmount)} // Disable if conditions are not met
        >
          Ready for Verification
        </Button>
      )}

      {/* add bondinfo moved here and always accessible for now*/}
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenBondModal}
        disabled={isSubmitting}
      >
        Add Bond Information
      </Button>
    </Box>

    {(bondDetails?.hasBeenPosted === false && (fileCount < 1 || powerCount < 1)) && (
      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
        You must upload 1 file and enter 1 power to submit for verification.
      </Typography>
    )}
    {(bondDetails?.hasBeenPosted === false &&
      Number(bondDetails?.totalBondAmount) !== Number(totalPowerAmount)) && (
      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
        Total power amount (${formatCurrency(totalPowerAmount)}) does not match expected bond amount (${formatCurrency(bondDetails?.totalBondAmount)}).
      </Typography>
    )}
      

     
      {/* Bond Info Modal */}
      <BondInfoModal
        open={isBondModalOpen}
        onClose={handleCloseBondModal}
        onSave={handleSaveBondInfo}
        bondInfo={selectedBondInfo}
        agentId={bondDetails?.executingAgentID}
        transactionId={bondDetails?.transferBondTransactionID}
        alreadyUsedPowerIds={
          (bondDetails?.powers || [])
            .map((b) => b.powerId)
            .filter((id) => id && id !== selectedBondInfo?.powerId)
        } // exclude the one being selected
      />

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmDialogOpen} onClose={handleCancelVerification}>
        <DialogTitle>Confirm Verification</DialogTitle>
        <DialogContent>
          <Typography>
            Please ensure that all information entered is correct. If any information is incorrect,
            Bankers will have to modify it, which may slow down the process.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelVerification} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmVerification} color="primary" variant="contained">
            {isSubmitting ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Files Modal */}
      <ViewFilesModal
        open={isViewFilesModalOpen}
        onClose={handleCloseViewFilesModal}
        bondDetails={bondDetails}
      />
    </>
  );
};

export default UploadPowersForm;
